// ============================================================
//  src/admin/pages/AdminPaymentsPage.js
// ============================================================
import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { listPayments } from '../api/adminClient';
import { PageHeader, Badge, Pagination } from '../components/UI';
import DataTable from '../components/DataTable';

function fmtNaira(n) { return '₦' + (Number(n) || 0).toLocaleString('en-NG', { maximumFractionDigits: 0 }); }
function fmtDate(d) {
  if (!d) return '—';
  const dt = new Date(d.replace(' ', 'T'));
  return isNaN(dt.getTime()) ? '—' : dt.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

const STATUS_TONE = { success: 'success', pending: 'warning', failed: 'danger', cancelled: 'muted' };

export default function AdminPaymentsPage() {
  const [payments, setPayments] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const limit = 30;

  const load = useCallback(() => {
    setLoading(true);
    listPayments({ page, limit, status, search })
      .then(res => { setPayments(res.data.payments); setTotal(res.data.total); })
      .catch(err => toast.error(err?.response?.data?.message || 'Could not load payments'))
      .finally(() => setLoading(false));
  }, [page, status, search]);

  useEffect(() => { load(); }, [load]);
  const totalPages = Math.max(1, Math.ceil(total / limit));

  const columns = [
    {
      key: 'user', label: 'User', primary: true,
      render: p => (
        <div>
          <div className="bf-cell-strong">{p.sFname} {p.sLname}</div>
          <div className="bf-cell-muted">{p.sEmail}</div>
        </div>
      ),
    },
    { key: 'plan', label: 'Plan', render: p => p.plName },
    { key: 'amount', label: 'Amount', render: p => fmtNaira(p.payAmount) },
    { key: 'pocketfi', label: 'PocketFi ID', render: p => <code className="bf-mono" style={{ fontSize: 11.5 }}>{p.payPocketfiId || '—'}</code> },
    { key: 'status', label: 'Status', render: p => <Badge tone={STATUS_TONE[p.payStatus] || 'warning'}>{p.payStatus}</Badge> },
    { key: 'date', label: 'Date', render: p => fmtDate(p.payCreatedAt) },
  ];

  return (
    <div className="bf-page">
      <PageHeader title="Payment History" subtitle={`${total.toLocaleString('en-NG')} recorded payments`} />

      <div className="bf-toolbar">
        <input
          className="bf-input"
          placeholder="Search by email or PocketFi ID…"
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }}
        />
        <select className="bf-select" value={status} onChange={e => { setStatus(e.target.value); setPage(1); }}>
          <option value="">All statuses</option>
          <option value="success">Success</option>
          <option value="pending">Pending</option>
          <option value="failed">Failed</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      <div className="bf-card">
        <div className="bf-card-body-flush">
          <DataTable
            columns={columns}
            rows={payments}
            keyField="payId"
            loading={loading}
            loadingMessage="Loading payments…"
            emptyMessage="No payments found."
          />
        </div>
      </div>

      <Pagination page={page} totalPages={totalPages} onChange={setPage} total={total} limit={limit} />
    </div>
  );
}